/**
 * ShiftWorkPage - Domain-agnostic shift work items page (DRR-232)
 *
 * Generic page for any industry's shift-based work items:
 * - Healthcare: Medication administration (eMAR)
 * - Finance: Scheduled reporting tasks
 * - Legal: Billable time blocks
 * - Construction: Scheduled safety inspections
 *
 * The page uses generic queries; domain-specific rendering is handled
 * by RendererRegistry, which resolves renderers from subgraph packages.
 *
 * Access control:
 *   - RequireScope("shift:read") on backend
 *   - Actor determines which items are visible
 */

import { useState, Suspense } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Alert,
  Button,
  Group,
  Badge,
  Card,
  Divider,
  LoadingOverlay,
  Table,
  ThemeIcon,
  Skeleton,
  Select,
  SegmentedControl,
  Collapse,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconRefresh,
  IconList,
  IconLayoutGrid,
  IconAlertCircle,
  IconPrinter,
} from '@tabler/icons-react';
import { MarPrintPanel } from '@ferp/subgraph-fhir';
// eslint-disable-next-line no-restricted-imports -- Data layer hook requires direct Apollo access
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

import { useAuth } from '@/auth';
import { useResolvedTerminology } from '@/hooks/useResolvedTerminology';
import { useRenderer } from '@/components/RendererRegistry';

// =============================================================================
// GraphQL - Uses generic queries from Track B (DRR-232)
// =============================================================================

const SHIFT_WORK_ITEMS = gql`
  query ShiftWorkItems($shiftId: ID!, $actorId: ID) {
    shiftWorkItems(shiftId: $shiftId, actorId: $actorId) {
      itemId
      itemType
      subjectId
      subjectName
      scheduledTime
      status
      priority
      instructions
      completedAt
      completedBy
    }
  }
`;

const ITEMS_NEEDING_ACTION = gql`
  query ItemsNeedingAction($itemType: String!) {
    itemsNeedingAction(itemType: $itemType) {
      itemId
      itemType
      subjectId
      subjectName
      scheduledTime
      status
      urgencyReason
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface ShiftWorkItem {
  itemId: string;
  itemType: string;
  subjectId: string;
  subjectName: string;
  scheduledTime: string;
  status: string;
  priority: string;
  instructions: string | null;
  completedAt: string | null;
  completedBy: string | null;
}

// Status constants (matches backend ShiftWorkItemStatus)
// Defined locally until codegen regenerates from updated schema
// Using const object to satisfy DRR-074 (no hardcoded status strings)
const ShiftWorkStatus = {
  Pending: 'PENDING',
  InProgress: 'IN_PROGRESS',
  Completed: 'COMPLETED',
  Missed: 'MISSED',
  Held: 'HELD',
  Refused: 'REFUSED',
} as const;

interface ActionableItem {
  itemId: string;
  itemType: string;
  subjectId: string;
  subjectName: string;
  scheduledTime: string;
  status: string;
  urgencyReason: string;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: string }) {
  const statusColors: Record<string, string> = {
    PENDING: 'yellow',
    IN_PROGRESS: 'blue',
    COMPLETED: 'green',
    MISSED: 'red',
    HELD: 'orange',
    REFUSED: 'gray',
  };

  const color = statusColors[status] ?? 'gray';
  const label = status.replace(/_/g, ' ').toLowerCase();

  return (
    <Badge color={color} variant="light" style={{ textTransform: 'capitalize' }}>
      {label}
    </Badge>
  );
}

// =============================================================================
// Priority Badge
// =============================================================================

function PriorityBadge({ priority }: { priority: string }) {
  const priorityColors: Record<string, string> = {
    CRITICAL: 'red',
    HIGH: 'orange',
    NORMAL: 'blue',
    LOW: 'gray',
  };

  const color = priorityColors[priority] ?? 'gray';

  return (
    <Badge color={color} variant="dot" size="sm">
      {priority.toLowerCase()}
    </Badge>
  );
}

// =============================================================================
// Work Item Card
// =============================================================================

function WorkItemCard({
  item,
  onSelect,
  selected,
  subjectLabel,
}: {
  item: ShiftWorkItem;
  onSelect: () => void;
  selected: boolean;
  subjectLabel: string;
}) {
  const scheduledTime = new Date(item.scheduledTime);
  const isOverdue = scheduledTime < new Date() && item.status === ShiftWorkStatus.Pending;

  return (
    <Card
      shadow={selected ? 'md' : 'sm'}
      padding="sm"
      radius="md"
      withBorder
      style={{
        cursor: 'pointer',
        borderColor: selected
          ? 'var(--mantine-color-blue-5)'
          : isOverdue
          ? 'var(--mantine-color-red-3)'
          : undefined,
        backgroundColor: selected
          ? 'var(--mantine-color-blue-0)'
          : isOverdue
          ? 'var(--mantine-color-red-0)'
          : undefined,
      }}
      onClick={onSelect}
    >
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm">
          {item.subjectName}
        </Text>
        <StatusBadge status={item.status} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">
        {item.itemType}
      </Text>

      <Divider my="xs" />

      <Group justify="space-between">
        <Group gap="xs">
          <IconClock size={12} />
          <Text size="xs" c={isOverdue ? 'red' : 'dimmed'}>
            {scheduledTime.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </Group>
        <PriorityBadge priority={item.priority} />
      </Group>
    </Card>
  );
}

// =============================================================================
// Items Needing Action Alert
// =============================================================================

function ItemsNeedingActionAlert({
  items,
  onViewAll,
}: {
  items: ActionableItem[];
  onViewAll: () => void;
}) {
  if (items.length === 0) return null;

  return (
    <Alert
      color="orange"
      title={`${items.length} item${items.length !== 1 ? 's' : ''} need attention`}
      icon={<IconAlertCircle size={18} />}
    >
      <Stack gap="xs">
        {items.slice(0, 3).map((item) => (
          <Group key={item.itemId} justify="space-between">
            <Text size="sm">
              {item.subjectName} - {item.itemType}
            </Text>
            <Badge size="xs" color="red">
              {item.urgencyReason}
            </Badge>
          </Group>
        ))}
        {items.length > 3 && (
          <Button size="xs" variant="subtle" onClick={onViewAll}>
            View all {items.length} items
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

// =============================================================================
// Detail Panel with Dynamic Renderer
// =============================================================================

function DetailPanel({
  item,
  rendererName,
}: {
  item: ShiftWorkItem;
  rendererName?: string;
}) {
  const DomainRenderer = useRenderer(rendererName);

  // If we have a domain-specific renderer, use it
  if (DomainRenderer) {
    return (
      <Suspense fallback={<Skeleton height={300} />}>
        <DomainRenderer entity={item as unknown as Record<string, unknown>} />
      </Suspense>
    );
  }

  // Generic fallback renderer
  const scheduledTime = new Date(item.scheduledTime);

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <div>
            <Text fw={600} size="lg">
              {item.subjectName}
            </Text>
            <Text size="sm" c="dimmed">
              {item.itemType}
            </Text>
          </div>
          <Stack gap="xs" align="flex-end">
            <StatusBadge status={item.status} />
            <PriorityBadge priority={item.priority} />
          </Stack>
        </Group>

        <Divider my="md" />

        <Stack gap="sm">
          <Group>
            <Text size="sm" c="dimmed" w={120}>
              Scheduled:
            </Text>
            <Text size="sm">{scheduledTime.toLocaleString()}</Text>
          </Group>

          {item.completedAt && (
            <Group>
              <Text size="sm" c="dimmed" w={120}>
                Completed:
              </Text>
              <Text size="sm">
                {new Date(item.completedAt).toLocaleString()}
                {item.completedBy && ` by ${item.completedBy}`}
              </Text>
            </Group>
          )}

          {item.instructions && (
            <>
              <Divider my="xs" />
              <Text size="sm" c="dimmed">
                Instructions:
              </Text>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {item.instructions}
              </Text>
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export function ShiftWorkPage() {
  const { user, tenant } = useAuth();
  const { getLabel } = useResolvedTerminology();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showNeedingAction, setShowNeedingAction] = useState(false);
  const [showMarPanel, setShowMarPanel] = useState(false);

  // Check if this is a healthcare tenant (FHIR subgraph)
  // DRR-303: Also check industry field from tenant config
  const isHealthcareTenant = tenant?.features?.['subgraph'] === 'fhir' ||
    tenant?.features?.['shiftWorkRenderer'] === 'FhirShiftWorkRenderer' ||
    (tenant as Record<string, unknown>)?.industry === 'assisted_living';

  // For demo, use a default shift ID - in production this would come from
  // shift selection or current shift detection
  const shiftId = 'current';

  // Subject label from terminology
  const subjectLabel = getLabel('subject') || 'Subject';

  // Fetch shift work items
  const { data, loading, error, refetch } = useQuery<{
    shiftWorkItems: ShiftWorkItem[];
  }>(SHIFT_WORK_ITEMS, {
    variables: { shiftId, actorId: user?.id },
    skip: !user,
    pollInterval: 30000,
  });

  // Fetch items needing action
  const { data: actionData } = useQuery<{
    itemsNeedingAction: ActionableItem[];
  }>(ITEMS_NEEDING_ACTION, {
    variables: { itemType: 'ALL' },
    skip: !user,
    pollInterval: 60000,
  });

  // Get renderer name from tenant metadata
  const rendererName = tenant?.features?.['shiftWorkRenderer'] as string | undefined;

  const workItems: ShiftWorkItem[] = data?.shiftWorkItems ?? [];
  const actionItems: ActionableItem[] = actionData?.itemsNeedingAction ?? [];
  const selectedItem = workItems.find((item) => item.itemId === selectedId);

  // Access check
  if (!user) {
    return (
      <Container size="md" py="xl">
        <Alert color="red" title="Authentication Required" icon={<IconAlertTriangle />}>
          <Text>Please log in to access shift work items.</Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Shift Work Items</Title>
            <Text c="dimmed" size="sm">
              Manage scheduled work for your current shift
            </Text>
          </div>
          <Group>
            <SegmentedControl
              size="xs"
              value={viewMode}
              onChange={(v) => setViewMode(v as 'grid' | 'table')}
              data={[
                { value: 'grid', label: <IconLayoutGrid size={14} /> },
                { value: 'table', label: <IconList size={14} /> },
              ]}
            />
            {isHealthcareTenant && (
              <Button
                variant={showMarPanel ? 'filled' : 'light'}
                leftSection={<IconPrinter size={16} />}
                onClick={() => setShowMarPanel(!showMarPanel)}
              >
                Print MAR
              </Button>
            )}
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
              loading={loading}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* MAR Print Panel (Healthcare only) */}
        {isHealthcareTenant && (
          <Collapse in={showMarPanel}>
            <MarPrintPanel
              tenantId={tenant?.id ?? ''}
              onPrint={() => {
                // Could track print events here
              }}
            />
          </Collapse>
        )}

        {/* Items needing action */}
        <ItemsNeedingActionAlert
          items={actionItems}
          onViewAll={() => setShowNeedingAction(true)}
        />

        {/* Error state */}
        {error && (
          <Alert color="red" title="Error Loading Items">
            {error.message}
          </Alert>
        )}

        {/* Empty state */}
        {!loading && workItems.length === 0 && (
          <Paper p="xl" withBorder ta="center">
            <ThemeIcon size="xl" variant="light" color="green" mx="auto" mb="md">
              <IconCheck size={24} />
            </ThemeIcon>
            <Title order={4}>No Work Items</Title>
            <Text c="dimmed" size="sm">
              No work items scheduled for this shift
            </Text>
          </Paper>
        )}

        {/* Main content */}
        {workItems.length > 0 && (
          <Group align="flex-start" gap="lg" grow>
            {/* Work items list/grid */}
            <Stack gap="sm" style={{ flex: 1 }}>
              <Text fw={500} size="sm" c="dimmed">
                {workItems.length} item{workItems.length !== 1 ? 's' : ''} this shift
              </Text>
              <LoadingOverlay visible={loading} />

              {viewMode === 'grid' ? (
                <Stack gap="sm">
                  {workItems.map((item) => (
                    <WorkItemCard
                      key={item.itemId}
                      item={item}
                      onSelect={() => setSelectedId(item.itemId)}
                      selected={selectedId === item.itemId}
                      subjectLabel={subjectLabel}
                    />
                  ))}
                </Stack>
              ) : (
                <Paper withBorder>
                  <Table striped highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{subjectLabel}</Table.Th>
                        <Table.Th>Type</Table.Th>
                        <Table.Th>Time</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Priority</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {workItems.map((item) => (
                        <Table.Tr
                          key={item.itemId}
                          onClick={() => setSelectedId(item.itemId)}
                          style={{ cursor: 'pointer' }}
                          bg={selectedId === item.itemId ? 'blue.0' : undefined}
                        >
                          <Table.Td>{item.subjectName}</Table.Td>
                          <Table.Td>{item.itemType}</Table.Td>
                          <Table.Td>
                            {new Date(item.scheduledTime).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Table.Td>
                          <Table.Td>
                            <StatusBadge status={item.status} />
                          </Table.Td>
                          <Table.Td>
                            <PriorityBadge priority={item.priority} />
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Paper>
              )}
            </Stack>

            {/* Detail panel */}
            <Paper
              p="md"
              withBorder
              style={{ flex: 1, minHeight: 400 }}
            >
              {selectedItem ? (
                <DetailPanel item={selectedItem} rendererName={rendererName} />
              ) : (
                <Stack justify="center" h="100%" align="center">
                  <ThemeIcon size="xl" variant="light" color="gray">
                    <IconClock size={24} />
                  </ThemeIcon>
                  <Text c="dimmed">Select an item to view details</Text>
                </Stack>
              )}
            </Paper>
          </Group>
        )}
      </Stack>
    </Container>
  );
}

export default ShiftWorkPage;
